import { css } from 'aphrodite';
import * as React from 'react';
import {
	IAllowInviteeLocationConfig,
	IInPersonMeetingLocation,
	IMeetingLocation,
	MeetingLocation,
} from '../../extViewmodels/sdk';
import { useEventLogging } from '../../models/Logging';
import { RadioButton } from '../../web/components/RadioButton';
import { urgentDate } from '../../web/styles/colors';
import { useMeeting } from '../contexts/meeting';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	setInviteeSelectedMeetingLocation: React.Dispatch<React.SetStateAction<MeetingLocation>>;
	inviteeSelectedMeetingLocation: MeetingLocation;
}

export const ScheduleMeetingLocation: React.FC<IProps> = ({
	className = '',
	setInviteeSelectedMeetingLocation,
	inviteeSelectedMeetingLocation,
}) => {
	const { meeting, updateLocationPhone, updateLocationVirtual, updateLocationInPerson } = useMeeting();
	const logger = useEventLogging();

	const onLocationChange = (location: IMeetingLocation) => async () => {
		try {
			setInviteeSelectedMeetingLocation(location._type);
			switch (location._type) {
				case MeetingLocation.Phone:
					await updateLocationPhone(location);
					break;
				case MeetingLocation.InPerson:
					await updateLocationInPerson(location);
					break;
				case MeetingLocation.Virtual:
					await updateLocationVirtual(location);
					break;
				default:
					break;
			}
		} catch (err) {
			logger.logApiError('SchedulerUpdateSelectedLocation-Error', err);
		}
	};

	const getDescription = (type: MeetingLocation) => {
		switch (type) {
			case MeetingLocation.Phone:
				return 'Phone Call';
			case MeetingLocation.InPerson:
				return 'In Person';
			case MeetingLocation.Virtual:
				return 'Virtual';
			default:
				return '';
		}
	};

	const sortedLocations = React.useMemo(() => {
		const locations = (meeting?.location as IAllowInviteeLocationConfig)?.locations || [];
		return [...locations].sort((a, b) => {
			// Put InPerson at the end
			if (a._type === MeetingLocation.InPerson) return 1;
			if (b._type === MeetingLocation.InPerson) return -1;
			return 0;
		});
	}, [meeting?.location]);

	return (
		<div className={`${css(styleSheet.container)} ${className}`}>
			<label className={css(styleSheet.inputLabel)} htmlFor='meeting-guests-input'>
				Meeting Location
				<span style={{ color: urgentDate }}>*</span>
			</label>
			{sortedLocations.map((location, i) => {
				const isInPerson = location._type === MeetingLocation.InPerson;
				const address = isInPerson ? (location as IInPersonMeetingLocation).address : undefined;

				return (
					<div key={`location-${i}`}>
						<RadioButton
							checked={inviteeSelectedMeetingLocation === location._type}
							id={`meeting-config-location-${location._type}`}
							name='meeting-config-location'
							onChange={onLocationChange(location)}
							className={css(styleSheet.locationOption)}
							iconClassName={css(styleSheet.locationOptionIcon)}
							labelClassName={css(styleSheet.locationOptionLabel)}
						>
							<span>
								{getDescription(location._type)}
								{isInPerson && address && <span className={css(styleSheet.addressText)}> ({address})</span>}
							</span>
						</RadioButton>
					</div>
				);
			})}
		</div>
	);
};
